import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCopy } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import FullscreenLoading from "../../Prescription/Components/FullScreenLoader";
import HeaderWithBack from "../../../components/HeaderWithBack";
import Confirm from "../../../components/modal/Confirm";
import { pendingDoctorLabel } from "../../../components/card/PendingPaymentCard";
import { formatCountdown, useCountdown } from "../../../hooks/useCountdown";
import {
  cancelPendingPayment,
  getPaymentStatus,
  PendingPayment,
} from "../../../services/pendingPaymentService";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";
import { decryptFromBackend } from "../../../utils/urlDecryptor";

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetCheckout = useCheckoutStore((s) => s.reset);
  const storeToken = useAuthStore((s) => s.token);

  const teleconsultId = searchParams.get("teleconsult_id");
  const encToken = searchParams.get("enc_token");
  const resultCode = searchParams.get("resultCode");

  const token = useMemo(() => {
    if (storeToken) return storeToken;
    try {
      return encToken ? decryptFromBackend(encToken) : "";
    } catch {
      return "";
    }
  }, [storeToken, encToken]);

  const [payment, setPayment] = useState<PendingPayment | null>(null);
  const [error, setError] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);
  const remaining = useCountdown(payment?.remaining_seconds);

  const fetchStatus = useCallback(async () => {
    if (!teleconsultId || !token) return;
    try {
      const data = await getPaymentStatus(teleconsultId, token);
      setPayment(data);

      if (data.payment_status === "paid") {
        localStorage.removeItem("teleconsult_disclaimer");
        resetCheckout();
        navigate(`/connect-doctor/${teleconsultId}/${encToken || data.enc_token}`, { replace: true });
      }
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        localStorage.removeItem("teleconsult_disclaimer");
        resetCheckout();
        navigate(`/connect-doctor/${teleconsultId}/${encToken ?? ""}`, { replace: true });
        return;
      }
      setError(true);
    }
  }, [teleconsultId, token, encToken, navigate, resetCheckout]);

  useEffect(() => {
    if (!teleconsultId || !token) {
      navigate("/home", { replace: true });
      return;
    }
    fetchStatus();
  }, [teleconsultId, token, fetchStatus, navigate]);

  useEffect(() => {
    if (payment?.payment_status !== "waiting") return;
    const timer = setInterval(fetchStatus, 5000);
    return () => clearInterval(timer);
  }, [payment?.payment_status, fetchStatus]);

  useEffect(() => {
    if (payment?.payment_status === "waiting" && remaining === 0) {
      fetchStatus();
    }
  }, [remaining === 0]);

  const handleCancel = async () => {
    if (!teleconsultId) return;
    setCancelling(true);
    try {
      await cancelPendingPayment(teleconsultId, token);
      resetCheckout();
      navigate("/home", { replace: true });
    } catch (err: any) {
      alert(err.response?.data?.message || "Gagal membatalkan pesanan");
      fetchStatus();
    } finally {
      setCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  const handleCopyVa = () => {
    if (!payment?.va) return;
    navigator.clipboard?.writeText(payment.va);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center px-6 text-center">
        <p className="text-gray-700 mb-4">Status pembayaran tidak dapat dimuat.</p>
        <button onClick={() => navigate("/home", { replace: true })} className="bg-teal-500 text-white px-6 py-2 rounded-lg">
          Kembali ke Beranda
        </button>
      </div>
    );
  }

  if (!payment || payment.payment_status === "paid") {
    return <FullscreenLoading />;
  }

  if (payment.payment_status !== "waiting") {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <HeaderWithBack title="Pembayaran" url="/home" />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-lg font-semibold text-gray-800 mb-2">
            {payment.payment_status === "expired" ? "Waktu Pembayaran Habis" : "Pesanan Dibatalkan"}
          </p>
          <p className="text-sm text-gray-600 mb-6">
            Pesanan konsultasi ini tidak dapat dibayar lagi. Silakan buat pesanan baru untuk berkonsultasi.
          </p>
          <button
            onClick={() => {
              resetCheckout();
              navigate("/home", { replace: true });
            }}
            className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold"
          >
            Pesan Ulang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-8">
      <HeaderWithBack title="Selesaikan Pembayaran" url="/home" />

      <div className="px-4 mt-4">
        {resultCode === "02" && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2 mb-3">
            Pembayaran belum diselesaikan di halaman Duitku. Lanjutkan pembayaran atau batalkan pesanan ini.
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-sm text-gray-500">Sisa waktu pembayaran</p>
          <p className="text-3xl font-bold text-amber-600 tabular-nums my-1">{formatCountdown(remaining)}</p>
          <p className="text-xs text-gray-400">
            Pesanan otomatis dibatalkan jika belum dibayar sampai waktu habis.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 mt-4 text-sm space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Konsultasi</span>
            <span className="text-gray-800 text-right">{pendingDoctorLabel(payment)}</span>
          </div>
          {payment.payment_method && (
            <div className="flex justify-between">
              <span className="text-gray-500">Metode</span>
              <span className="text-gray-800">{payment.payment_method}</span>
            </div>
          )}
          {payment.va && (
            <div className="flex justify-between items-center">
              <span className="text-gray-500">No. Virtual Account</span>
              <button onClick={handleCopyVa} className="flex items-center gap-1 font-semibold text-gray-800">
                {payment.va} <FiCopy className="text-teal-500" />
              </button>
            </div>
          )}
          {copied && <p className="text-xs text-teal-600 text-right">Nomor VA disalin</p>}
          <hr />
          <div className="flex justify-between font-semibold">
            <span>Total Bayar</span>
            <span className="text-teal-600">{formatRupiah(payment.total)}</span>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Halaman ini akan otomatis berpindah setelah pembayaran diterima.
        </p>

        <div className="flex flex-col gap-2 mt-4">
          <button
            onClick={() => payment.payment_url && (window.location.href = payment.payment_url)}
            disabled={!payment.payment_url}
            className="bg-teal-500 text-white py-2.5 rounded-xl font-semibold disabled:opacity-50"
          >
            Buka Halaman Pembayaran
          </button>
          <button
            onClick={() => setShowCancelConfirm(true)}
            className="border border-red-300 text-red-500 py-2.5 rounded-xl font-medium"
          >
            Batalkan Pesanan
          </button>
          <button onClick={() => navigate("/home")} className="text-gray-500 text-sm py-1">
            Kembali ke Beranda
          </button>
        </div>
      </div>

      <Confirm
        show={showCancelConfirm}
        title="Batalkan Pesanan?"
        message="Pesanan konsultasi yang belum dibayar akan dibatalkan. Anda dapat membuat pesanan baru setelahnya."
        confirmText="Batalkan"
        cancelText="Kembali"
        onConfirm={handleCancel}
        onCancel={() => setShowCancelConfirm(false)}
        loading={cancelling}
      />
    </div>
  );
};

export default PaymentSuccess;